<template>
  <v-navigation-drawer
    id="app-drawer"
    v-model="drawer"
    :mini-variant="mini"
    mini-variant-width="64"
    app
    dark
    width="248"
    class="fill-height"
  >
    <div
      v-if="!mini"
      class="drawer-logo"
    >
      <v-img
        :src="logo"
        width="176px"
        contain
        aspect-ratio="3.8"
      />
    </div>

    <v-list
      nav
      dense
      class="drawer-list"
    >
      <v-list-group
        v-if="$store.getters['user/hasRight']('product_v2')"
        :value="true"
        color="white"
        class="drawer-group"
      >
        <template #activator>
          <v-list-item-icon v-if="mini">
            <v-icon>mdi-shape-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="drawer-group-title">
              {{ $t('ProductsV2.menu.title') }}
            </v-list-item-title>
          </v-list-item-content>
        </template>
        <v-list-item
          v-for="([icon, text, link], i) in productsV2Items"
          :key="i"
          :to="link"
          :disabled="!link"
          :title="$t(text)"
          class="drawer-item drawer-subitem"
          link
        >
          <v-list-item-icon v-if="mini">
            <v-icon>{{ icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="drawer-label">
            {{ $t(text) }}
            <span
              v-if="!link"
              class="drawer-soon"
            >{{ $t('Common.soon') }}</span>
          </v-list-item-title>
        </v-list-item>
      </v-list-group>

      <div class="drawer-gap" />

      <v-list-item
        v-for="([icon, text, link], i) in filteredItems"
        :key="i"
        :to="link"
        :title="$tc(text, 2)"
        class="drawer-item"
        link
      >
        <v-list-item-icon v-if="mini">
          <v-icon>{{ icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-title class="drawer-label">
          {{ $tc(text, 2) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <template #append>
      <div class="drawer-collapse">
        <v-btn
          icon
          small
          @click="toggleMini"
        >
          <v-icon>{{ mini ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left' }}</v-icon>
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
// Utilities
import {
  mapMutations,
} from 'vuex'

export default {
  name: 'CoreDrawer',

  data: () => ({
    items: [
      ['mdi-brain', 'Product.menu', '/products/list', 'products:read'],
      // Bulk termsheet import (BP-283) is intentionally NOT in the menu: access is
      // restricted to a single user via the bulk_termsheet_import scope, who reaches
      // it by direct link (/product-import-batches). The route stays registered.
      ['mdi-palette-swatch-outline', 'Product.productType', '/products/types', 'product-types:read'],
      ['mdi-swap-horizontal', 'Transaction.menu', '/assetManagerTransaction/list', 'asset-manager-transactions:read'],
      ['mdi-account-multiple', 'AssetManagers.menu', '/assetManagers', 'assetManagers:read'],
      ['mdi-account-details', 'Roles.menu', '/roles', 'roles:read'],
      ['mdi-email', 'Emails.menu', '/emails', 'emails:read'],
      ['mdi-comment-account', 'Common.businessIntroducer', '/business-introducers', 'business-introducers:read'],
      ['mdi-domain', 'Issuers.menu', '/issuers', 'issuers:read'],
      ['mdi-tune-variant', 'PricingConfig.menu', '/pricing-config', 'issuers:update'],
      ['mdi-cash-100', 'Common.pricing', '/pricing', 'pricings:read'],
      ['mdi-map-marker', 'Common.distributionLocation', '/distribution-locations', 'distribution-locations:read'],
      ['mdi-bank-transfer-in', 'Brokers.menu', '/brokers', 'brokers:read'],
      ['mdi-bank', 'Custodians.menu', '/custodians', 'custodians:read'],
      ['mdi-view-list', 'Stocks.menu', '/stocks', 'stocks:read'],
      ['mdi-email', 'Templates.menu', '/templates', 'templates:read'],
      ['mdi-file-tree', 'Common.altitudeEntities', '/altitude-entities', 'altitude-entities:read'],
      ['mdi-text-box-multiple', 'TemplateProduct.menu', '/templateProducts'],
      ['mdi-file-table-box-multiple', 'InvoiceTemplates.menu', '/invoice', 'invoice-templates:read'],
      ['mdi-calendar-remove', 'Stocks.holidays.menu', '/holidays', 'holidays:read'],
      ['mdi-calendar-multiselect', 'Observations', '/observations', 'product-schedule-dates:read'],
      ['mdi-cash-sync', 'Common.price', '/prices', undefined],
      ['mdi-medical-bag', 'Common.diagnostic', '/diagnostics', 'diagnostics:read'],
      ['mdi-format-list-bulleted-type', 'Common.reportFields', '/report-fields', 'report-fields:read'],
      ['mdi-tune', 'Common.corporateActions', '/corporate-actions', 'stocks:read'],
      ['mdi-security-network', 'Regulators', '/regulators', 'regulators:read'],
      ['mdi-lightbulb', 'Product.productType', '/products/types', 'products:update'],
    ],
    productsV2Items: [
      ['mdi-view-grid-outline', 'ProductsV2.menu.catalogue', '/products-v2/catalogue'],
      ['mdi-file-document-edit-outline', 'ProductsV2.menu.newProduct', '/products-v2/new'],
      ['mdi-puzzle-outline', 'ProductsV2.menu.bricks', '/products-v2/bricks'],
      ['mdi-sitemap-outline', 'ProductsV2.menu.composer', '/products-v2/composer'],
    ],
    logo: '/img/logo.png'
  }),
  computed: {
    drawer: {
      get() { return this.$store.state.theme.drawer },
      set(value) { this.setDrawer(value) }
    },

    mini() {
      return this.$store.state.theme.mini
    },

    filteredItems() {
      // Removes pages when the user does not have the required right
      return this.items?.filter(
        ([, , , right]) => !right || this.$store.getters['user/hasRight'](right)
      )
    }
  },
  mounted () {
    this.onResponsiveInverted()
    window.addEventListener('resize', this.onResponsiveInverted)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResponsiveInverted)
  },
  methods: {
    ...mapMutations('theme', ['setDrawer', 'toggleDrawer', 'toggleMini']),
    onResponsiveInverted () {
      if (window.innerWidth < 991) {
        this.responsive = true
      } else {
        this.responsive = false
      }
    }
  }
}
</script>

<style lang="scss">
    #app-drawer {
        background: linear-gradient(180deg, #1c3664 0%, #11234a 55%, #0b1733 100%);

        .drawer-logo {
            padding: 18px 20px 16px;
            border-bottom: 1px solid rgba(255, 255, 255, .08);
        }

        .drawer-list {
            background: transparent;
            padding: 8px;
        }

        .drawer-item {
            min-height: 36px;
            margin-bottom: 2px;
            border-radius: 6px;
            padding: 6px 12px;
            color: rgba(255, 255, 255, .85) !important;

            &::before {
                border-radius: 6px;
            }

            &:hover::before {
                opacity: .08;
            }
        }

        .drawer-subitem {
            padding-left: 28px;
        }

            .drawer-item.v-list-item--active {
            color: #fff !important;
            background: rgba(255, 255, 255, .12);
            box-shadow: inset 3px 0 0 #4fc3f7;

            &::before {
                opacity: 0 !important;
            }
        }

        .drawer-group > .v-list-group__header {
            min-height: 38px;
            border-radius: 6px;
            padding: 0 12px;
            background: rgba(255, 255, 255, .06);
            margin-bottom: 2px;
        }

        .drawer-group-title {
            font-size: 14px;
            font-weight: 500;
        }

        .drawer-label {
            font-size: 14px;
            font-weight: 400;
            line-height: 1.3;
            white-space: normal;
        }

        .drawer-soon {
            font-size: 10px;
            margin-left: 6px;
            padding: 1px 6px;
            border-radius: 8px;
            background: rgba(255, 255, 255, .18);
        }

        .drawer-gap {
            height: 12px;
        }

        .drawer-collapse {
            display: flex;
            justify-content: flex-end;
            padding: 8px 12px;
            border-top: 1px solid rgba(255, 255, 255, .08);
        }

        &.v-navigation-drawer--mini-variant {
            .drawer-subitem {
                padding-left: 12px;
            }

            .drawer-collapse {
                justify-content: center;
            }
        }
    }
</style>
